import React, { useLayoutEffect, useRef, useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import { formatPeriodRangeLabel, PeriodValue } from '../../utils/periodUtils';
import SegmentedToggle from './SegmentedToggle';

export type { PeriodValue };

interface PeriodToggleProps {
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  size?: 'sm' | 'md';
  className?: string;
  /** Show adaptive date range next to the toggle (dashboard). */
  showRange?: boolean;
}

type RangeMode = 'full' | 'compact' | 'hidden';

/** px budgets for the range label (full → compact → hide). */
function getRangeLayout(): { show: boolean; allowFull: boolean; budgetPx: number } {
  if (typeof window === 'undefined') return { show: false, allowFull: false, budgetPx: 0 };
  if (window.matchMedia('(min-width: 1280px)').matches) {
    return { show: true, allowFull: true, budgetPx: 18 * 16 }; // xl+
  }
  if (window.matchMedia('(min-width: 640px)').matches) {
    return { show: true, allowFull: false, budgetPx: 12 * 16 }; // sm–lg: compact only
  }
  return { show: false, allowFull: false, budgetPx: 0 }; // < sm → hide
}

/**
 * Picks full / compact / hidden from text width vs viewport budget.
 * Avoids mid-label ellipsis ("7 ao…").
 */
const AdaptivePeriodRange: React.FC<{ full: string; compact: string }> = ({ full, compact }) => {
  const fullProbeRef = useRef<HTMLSpanElement>(null);
  const compactProbeRef = useRef<HTMLSpanElement>(null);
  const [mode, setMode] = useState<RangeMode>('compact');

  useLayoutEffect(() => {
    const update = () => {
      const { show, allowFull, budgetPx } = getRangeLayout();
      if (!show || budgetPx <= 0) {
        setMode('hidden');
        return;
      }
      const fullW = fullProbeRef.current?.offsetWidth ?? Number.POSITIVE_INFINITY;
      const compactW = compactProbeRef.current?.offsetWidth ?? Number.POSITIVE_INFINITY;
      if (allowFull && fullW <= budgetPx) setMode('full');
      else if (compactW <= budgetPx) setMode('compact');
      else setMode('hidden');
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [full, compact]);

  const visible = mode === 'full' ? full : mode === 'compact' ? compact : null;

  return (
    <span className="relative hidden sm:inline-flex h-5 items-center" title={full}>
      <span
        ref={fullProbeRef}
        className="pointer-events-none absolute left-0 top-0 whitespace-nowrap text-sm opacity-0"
        aria-hidden
      >
        {full}
      </span>
      <span
        ref={compactProbeRef}
        className="pointer-events-none absolute left-0 top-0 whitespace-nowrap text-sm opacity-0"
        aria-hidden
      >
        {compact}
      </span>
      {visible ? (
        <span className="inline-flex h-7 items-center whitespace-nowrap rounded-full bg-gray-100/90 px-2.5 text-xs font-medium text-gray-600">
          {visible}
        </span>
      ) : null}
    </span>
  );
};

const PeriodToggle: React.FC<PeriodToggleProps> = ({
  value,
  onChange,
  size = 'md',
  className = '',
  showRange = false,
}) => {
  const { t, language } = useI18n();

  const options: Array<{ key: PeriodValue; label: string }> = [
    { key: 'month', label: t('common.thisMonth') },
    { key: 'year', label: t('common.thisYear') },
    { key: 'total', label: t('common.total') },
  ];

  const rangeFull = showRange ? formatPeriodRangeLabel(value, language, new Date(), 'full') : null;
  const rangeCompact = showRange
    ? formatPeriodRangeLabel(value, language, new Date(), 'compact')
    : null;

  if (!showRange) {
    return (
      <SegmentedToggle
        value={value}
        onChange={onChange}
        options={options}
        size={size}
        ariaLabel={t('common.allPeriods')}
        fullWidthOnMobile
        className={`w-full sm:w-auto ${className}`}
      />
    );
  }

  return (
    <div
      className={`flex w-full min-w-0 max-w-full flex-row flex-nowrap items-center justify-end gap-x-2 sm:w-auto sm:gap-x-3 ${className}`}
    >
      {rangeFull && rangeCompact ? (
        <AdaptivePeriodRange full={rangeFull} compact={rangeCompact} />
      ) : null}
      <SegmentedToggle
        value={value}
        onChange={onChange}
        options={options}
        size={size}
        ariaLabel={t('common.allPeriods')}
        fullWidthOnMobile
        className="w-full sm:w-auto"
      />
    </div>
  );
};

export default PeriodToggle;
